/**
 * Solem analytics wrapper for find-it-later (app.solem.ai).
 *
 * Single chokepoint for all PostHog interaction. Implements the contract in
 * Personal/solem/Doing/analytics/posthog-funnel-plan.md (v5):
 *   - super-properties: surface, capture_source, app_env, app_version,
 *     build_sha, schema_version
 *   - identity: alias() ONLY on confirmed new account, identify() always
 *   - bootstrap: { distinctID, isIdentifiedID: false } from ?ph_did URL handoff
 *   - localStorage-backed first/last UTM persistence (90d implicit via localStorage)
 *   - dev-loud failure: console.warn if event fires before init()
 *
 * Raw `posthog.capture` MUST NOT be called from app code. Use `analytics.track`.
 * If you need a new event, add it to SolemEvent below.
 */

import posthog from 'posthog-js';

// ── Types ────────────────────────────────────────────────────────────────

/**
 * Canonical event catalog (mirrors §3 of the plan). Expand as new surfaces
 * are instrumented. Keeping properties as Record<string, unknown> for v1 —
 * the runtime wrapper auto-injects required context (surface, path, plan).
 * Per-event property types can be added in a later pass.
 */
export type SolemEvent =
  // pageview & navigation
  | '$pageview'
  // auth
  | 'signup_started'
  | 'signup_method_picked'
  | 'signup_completed'
  | 'signin_started'
  | 'signin_completed'
  | 'signout'
  | 'magic_link_requested'
  | 'magic_link_consumed'
  // onboarding
  | 'onboarding_started'
  | 'onboarding_step_viewed'
  | 'onboarding_step_completed'
  | 'onboarding_step_back'
  | 'onboarding_field_changed'
  | 'onboarding_skipped'
  | 'onboarding_completed'
  | 'onboarding_resumed'
  | 'extension_auth_started'
  | 'extension_auth_completed'
  | 'mcp_setup_started'
  | 'mcp_setup_completed'
  // core actions
  | 'save_created'
  | 'save_opened'
  | 'save_deleted'
  | 'save_edited'
  | 'saves_list_filtered'
  | 'saves_list_sorted'
  | 'add_modal_opened'
  | 'add_modal_closed'
  // search
  | 'search_run'
  | 'search_result_clicked'
  | 'search_zero_results'
  // chat & MCP
  | 'chat_opened'
  | 'chat_message_sent'
  | 'chat_response_received'
  | 'chat_response_failed'
  | 'chat_source_clicked'
  | 'chat_thread_deleted'
  | 'chat_thread_renamed'
  // caps
  | 'cap_approaching_80'
  | 'cap_hit'
  | 'usage_banner_viewed'
  | 'cap_modal_viewed'
  | 'cap_modal_dismissed'
  // settings
  | 'settings_opened'
  | 'settings_section_viewed'
  | 'account_field_changed'
  | 'api_key_generated'
  | 'api_key_revoked'
  | 'mcp_token_regenerated'
  | 'account_delete_initiated'
  | 'account_delete_confirmed'
  | 'account_delete_failed'
  // upgrade & billing
  | 'upgrade_intent'
  | 'upgrade_page_viewed'
  | 'upgrade_plan_picked'
  | 'checkout_redirect_started'
  | 'checkout_success_returned'
  | 'checkout_abandoned'
  | 'portal_returned'
  | 'trial_expired_viewed'
  | 'payment_failed_banner_viewed'
  | 'downgrade_notice_viewed'
  | 'founding_seats_low_viewed'
  | 'home_upgrade_cta_clicked'
  // legacy 404
  | 'not_found_view';

export interface IdentifyProps {
  /** Mutable person properties — overwritten on every identify call. */
  set?: Record<string, unknown>;
  /** Immutable first-touch person properties — set once, never overwritten. */
  setOnce?: Record<string, unknown>;
}

export interface FirstTouchSnapshot {
  first_utm_source?: string;
  first_utm_medium?: string;
  first_utm_campaign?: string;
  first_utm_content?: string;
  first_utm_term?: string;
  first_landing_url?: string;
  first_referrer?: string;
  first_landing_at?: string;
}

export interface LastTouchSnapshot {
  last_utm_source?: string;
  last_utm_medium?: string;
  last_utm_campaign?: string;
  last_utm_content?: string;
  last_utm_term?: string;
  last_landing_url?: string;
  last_referrer?: string;
  last_landing_at?: string;
}

// ── Upgrade-intent types ─────────────────────────────────────────────────

/**
 * Locked `trigger_source` enum from §3.9. Every upgrade entry point MUST
 * pass one of these. Extend the enum (here and in the plan) when adding a
 * new entry surface — never invent an ad-hoc value.
 */
export type UpgradeTriggerSource =
  | 'banner_amber'
  | 'banner_red'
  | 'cap_modal_saves'
  | 'cap_modal_chat'
  | 'cap_modal_mcp'
  | 'settings_plan'
  | 'onboarding_pro_pick'
  | 'upgrade_page_cta'
  | 'marketing_cta'
  | 'pricing_card_marketing'
  | 'navbar_get_started'
  | 'home_logged_out_cta'
  | 'trial_expired_screen'
  | 'payment_failed_banner'
  | 'downgrade_notice'
  | 'portal_return_resume'
  | 'founding_seats_low'
  | 'email_trial_d7'
  | 'email_trial_d11'
  | 'email_trial_d13'
  | 'email_win_back'
  | 'email_payment_failed'
  | 'email_renewal'
  | 'email_cancel'
  | 'extension_cap'
  | 'direct';

export interface UpgradeIntentProps {
  trigger_detail?: string;
  current_plan?: string;
  current_interval?: 'month' | 'year' | null;
  usage_state?: Record<string, unknown>;
  cap_type?: 'saves' | 'ai_message' | 'mcp_call';
  cap_pct?: number;
  used?: number;
  limit?: number;
  destination_path?: string;
}

// ── Module state ─────────────────────────────────────────────────────────

let initialized = false;

// Trim defensively: env vars pasted via dashboard UIs (Vercel/Netlify/Railway)
// can carry a trailing newline that gets baked into the bundle. PostHog silently
// rejects events for invalid tokens.
const POSTHOG_KEY = (
  (import.meta.env.VITE_POSTHOG_KEY as string | undefined) ||
  'phc_NBH633hYO9TaB54BkModM7q33kdrTUegoLByuyMCca5'
).trim();

const POSTHOG_HOST = '/ingest';
const POSTHOG_UI_HOST = 'https://us.posthog.com';

const LS_KEYS = {
  firstUtmSource: 'solem_first_utm_source',
  firstUtmMedium: 'solem_first_utm_medium',
  firstUtmCampaign: 'solem_first_utm_campaign',
  firstUtmContent: 'solem_first_utm_content',
  firstUtmTerm: 'solem_first_utm_term',
  firstLandingUrl: 'solem_first_landing_url',
  firstReferrer: 'solem_first_referrer',
  firstLandingAt: 'solem_first_landing_at',
  lastUtmSource: 'solem_last_utm_source',
  lastUtmMedium: 'solem_last_utm_medium',
  lastUtmCampaign: 'solem_last_utm_campaign',
  lastUtmContent: 'solem_last_utm_content',
  lastUtmTerm: 'solem_last_utm_term',
  lastLandingUrl: 'solem_last_landing_url',
  lastReferrer: 'solem_last_referrer',
  lastLandingAt: 'solem_last_landing_at',
} as const;

// ── Helpers ──────────────────────────────────────────────────────────────

function readPhDidFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const v = params.get('ph_did')?.trim();
  return v || undefined;
}

function alreadyHasPostHogCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split(';').some((c) => c.trim().startsWith('ph_'));
}

function safeReadLs(key: string): string | undefined {
  try {
    return window.localStorage.getItem(key) || undefined;
  } catch {
    return undefined;
  }
}

function safeWriteLs(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* localStorage full / disabled — ignore */
  }
}

function appEnv(): 'development' | 'preview' | 'production' {
  const mode = import.meta.env.MODE;
  if (mode === 'production') return 'production';
  if (mode === 'preview') return 'preview';
  return 'development';
}

function appVersion(): string {
  // Vite injects this from package.json at build time via define in vite.config
  // Fallback to '0.0.0' if missing rather than blocking init.
  return (import.meta.env.VITE_APP_VERSION as string | undefined) || '0.0.0';
}

function buildSha(): string {
  return (import.meta.env.VITE_BUILD_SHA as string | undefined) || 'dev';
}

// ── Public API ───────────────────────────────────────────────────────────

export const analytics = {
  /**
   * Initialize PostHog once on app boot. Reads ?ph_did= from URL on first
   * arrival (no existing PostHog cookie) for cross-subdomain identity handoff.
   *
   * Safe to call once; subsequent calls are no-ops.
   */
  init(): void {
    if (initialized) return;
    if (!POSTHOG_KEY) {
      console.warn('[analytics] no POSTHOG_KEY — skipping init');
      return;
    }
    if (typeof window === 'undefined') return;

    // URL handoff is consumed ONLY on first session (no existing ph_ cookie).
    // Prevents identity poisoning if someone shares a link with their ph_did.
    const phDid = !alreadyHasPostHogCookie() ? readPhDidFromUrl() : undefined;

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      person_profiles: 'identified_only',
      capture_pageview: false, // SPA — we fire manually via usePageviewTracker
      capture_pageleave: true,
      autocapture: true,
      cross_subdomain_cookie: true,
      debug: appEnv() === 'development',
      ...(phDid
        ? {
            bootstrap: {
              distinctID: phDid,
              isIdentifiedID: false,
            },
          }
        : {}),
    });

    // Super-properties on every event — registered AFTER init so first
    // events carry them. surface/capture_source are app-side defaults.
    posthog.register({
      surface: 'app',
      capture_source: 'client',
      app_env: appEnv(),
      app_version: appVersion(),
      build_sha: buildSha(),
      schema_version: '1',
    });

    initialized = true;

    if (appEnv() === 'development') {
      // eslint-disable-next-line no-console
      console.log('[analytics] initialized', {
        distinct_id: posthog.get_distinct_id(),
        bootstrapped_from_url: Boolean(phDid),
      });
    }
  },

  /**
   * Fire a typed event. Wrapper adds `path` and any other auto-injected
   * context. Properties beyond required context are passed through.
   */
  track(event: SolemEvent, properties: Record<string, unknown> = {}): void {
    if (!initialized) {
      console.warn(`[analytics] track('${event}') before init — dropped`);
      return;
    }
    const path = typeof window !== 'undefined' ? window.location.pathname : undefined;
    posthog.capture(event, { path, ...properties });
  },

  /**
   * Link the current anonymous distinct_id to a real user_id. Call ONLY when
   * the server confirms a new account was created. Calling on signin will
   * corrupt the identity graph (PostHog merges).
   */
  alias(userId: string): void {
    if (!initialized) {
      console.warn(`[analytics] alias before init — dropped`);
      return;
    }
    posthog.alias(userId);
  },

  /**
   * Set the canonical user_id and update person properties. Idempotent.
   * Translates `{ set, setOnce }` to PostHog's positional API:
   *   posthog.identify(distinctId, userPropertiesToSet, userPropertiesToSetOnce)
   * Verified against posthog-js v1.x source.
   */
  identify(userId: string, props: IdentifyProps = {}): void {
    if (!initialized) {
      console.warn(`[analytics] identify before init — dropped`);
      return;
    }
    posthog.identify(userId, props.set, props.setOnce);
  },

  /** Clear the current distinct_id. Call on signout. */
  reset(): void {
    if (!initialized) return;
    posthog.reset();
  },

  /** Current distinct_id — used to append ?ph_did= on outbound links. */
  getDistinctId(): string | undefined {
    if (!initialized) return undefined;
    try {
      return posthog.get_distinct_id();
    } catch {
      return undefined;
    }
  },

  /**
   * Read first-touch UTM snapshot from localStorage. Returns empty object
   * if no first-touch is recorded yet (user hasn't landed via a UTM'd link).
   */
  getFirstTouch(): FirstTouchSnapshot {
    if (typeof window === 'undefined') return {};
    return {
      first_utm_source: safeReadLs(LS_KEYS.firstUtmSource),
      first_utm_medium: safeReadLs(LS_KEYS.firstUtmMedium),
      first_utm_campaign: safeReadLs(LS_KEYS.firstUtmCampaign),
      first_utm_content: safeReadLs(LS_KEYS.firstUtmContent),
      first_utm_term: safeReadLs(LS_KEYS.firstUtmTerm),
      first_landing_url: safeReadLs(LS_KEYS.firstLandingUrl),
      first_referrer: safeReadLs(LS_KEYS.firstReferrer),
      first_landing_at: safeReadLs(LS_KEYS.firstLandingAt),
    };
  },

  /** Read last-touch snapshot. Refreshed on every UTM'd landing. */
  getLastTouch(): LastTouchSnapshot {
    if (typeof window === 'undefined') return {};
    return {
      last_utm_source: safeReadLs(LS_KEYS.lastUtmSource),
      last_utm_medium: safeReadLs(LS_KEYS.lastUtmMedium),
      last_utm_campaign: safeReadLs(LS_KEYS.lastUtmCampaign),
      last_utm_content: safeReadLs(LS_KEYS.lastUtmContent),
      last_utm_term: safeReadLs(LS_KEYS.lastUtmTerm),
      last_landing_url: safeReadLs(LS_KEYS.lastLandingUrl),
      last_referrer: safeReadLs(LS_KEYS.lastReferrer),
      last_landing_at: safeReadLs(LS_KEYS.lastLandingAt),
    };
  },

  /**
   * Capture UTMs from the current URL into localStorage. Call on every
   * pageview that includes UTM params; safe to call on every pageview
   * regardless (no-ops if no UTMs).
   *
   * - first_utm_* is written ONLY if not already set (first-touch immutable)
   * - last_utm_* is always overwritten with current values
   */
  captureUtmsFromUrl(): void {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    if (!utmSource) return; // no UTMs on this URL

    const utmFields: Array<[string, string]> = [
      [LS_KEYS.lastUtmSource, utmSource],
      [LS_KEYS.lastUtmMedium, params.get('utm_medium') || ''],
      [LS_KEYS.lastUtmCampaign, params.get('utm_campaign') || ''],
      [LS_KEYS.lastUtmContent, params.get('utm_content') || ''],
      [LS_KEYS.lastUtmTerm, params.get('utm_term') || ''],
      [LS_KEYS.lastLandingUrl, window.location.href],
      [LS_KEYS.lastReferrer, document.referrer || ''],
      [LS_KEYS.lastLandingAt, new Date().toISOString()],
    ];
    for (const [k, v] of utmFields) safeWriteLs(k, v);

    // First-touch: write only if not present yet
    if (!safeReadLs(LS_KEYS.firstUtmSource)) {
      const firstFields: Array<[string, string]> = [
        [LS_KEYS.firstUtmSource, utmSource],
        [LS_KEYS.firstUtmMedium, params.get('utm_medium') || ''],
        [LS_KEYS.firstUtmCampaign, params.get('utm_campaign') || ''],
        [LS_KEYS.firstUtmContent, params.get('utm_content') || ''],
        [LS_KEYS.firstUtmTerm, params.get('utm_term') || ''],
        [LS_KEYS.firstLandingUrl, window.location.href],
        [LS_KEYS.firstReferrer, document.referrer || ''],
        [LS_KEYS.firstLandingAt, new Date().toISOString()],
      ];
      for (const [k, v] of firstFields) safeWriteLs(k, v);
    }
  },

  /**
   * Generate an upgrade_intent_id (UUID v4), fire the canonical `upgrade_intent`
   * event with the locked `trigger_source` enum, and return the UUID. The
   * caller is responsible for threading it onto the /upgrade URL — typically:
   *
   *   const id = analytics.beginUpgradeIntent('banner_red', { cap_type: 'saves' });
   *   navigate(`/upgrade?upgrade_intent_id=${id}&from=banner_red`);
   *
   * The same UUID then flows: URL → /upgrade page → checkout body → Stripe
   * metadata → webhook → server emits subscription_created with this id. That
   * thread is what makes the analytics_plan.md §1.7 "killer feature" work.
   */
  beginUpgradeIntent(
    trigger_source: UpgradeTriggerSource,
    props: UpgradeIntentProps = {},
  ): string {
    if (!initialized) {
      console.warn(`[analytics] beginUpgradeIntent('${trigger_source}') before init`);
      return generateUuid();
    }
    const upgrade_intent_id = generateUuid();
    const source_path = typeof window !== 'undefined' ? window.location.pathname : undefined;
    posthog.capture('upgrade_intent', {
      upgrade_intent_id,
      trigger_source,
      source_path,
      ...props,
    });
    return upgrade_intent_id;
  },

  /** Lower-level escape hatch. Avoid in favor of `track()`. */
  _raw: posthog,
};

/**
 * Canonical cap period identifier. Caps reset monthly; we want one stable
 * id per (user, calendar month UTC) so PostHog can dedupe "user hit cap"
 * within a single billing-ish window. Plan §3.10.
 *
 * Format: 'YYYY-MM' (UTC). Free-tier matches this; paid users on a
 * subscription cycle might drift +/- a few days but the bucketing is
 * close enough for funnel analysis.
 */
export function capPeriodId(at: Date = new Date()): string {
  const y = at.getUTCFullYear();
  const m = String(at.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

// Native crypto.randomUUID is widely supported; fall back to a v4 polyfill
// for older browsers (Safari < 15.4) so upgrade flow never breaks on a UUID.
function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 fallback
  const rand = () => Math.floor(Math.random() * 16).toString(16);
  let s = '';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) s += '-';
    else if (i === 14) s += '4';
    else if (i === 19) s += (8 + Math.floor(Math.random() * 4)).toString(16);
    else s += rand();
  }
  return s;
}
